// Script-review + GEO optimizer pack types live in @discovr/types (the BE/AI
// contract source of truth) — re-exported here so existing `from './types'`
// imports in this directory don't need to change. These were previously
// declared twice (here and in @discovr/types) — first fixed for the 13 Geo*
// types, now extended to the non-GEO script-review types, which had drifted
// into the exact same duplicate-declaration pattern. UI-only helpers
// (severityTone, riskTone, toRecommendation, etc.) stay local below — they
// embed Tailwind class strings and aren't part of the BE/AI contract.
export type {
  GeoTranslated,
  GeoTitleOption,
  GeoQABlock,
  GeoDescription,
  GeoChapter,
  GeoPinnedComment,
  GeoOnScreenCue,
  GeoVoiceMatch,
  GeoNeedsConfirmation,
  GeoAuthenticity,
  GeoPack,
  GeoPackWrapper,
  GeoAiEnvelope,
  ScriptReviewTaskResponse,
  ScriptReviewAiEnvelope,
  Severity,
  LineSeverity,
  ScoreBreakdown,
  VariantMeta,
  TranslatedString,
  TranslatableField,
  ScriptReviewResult,
  Recommendation,
  Strength,
} from '@discovr/types';
export { VARIANT_INSUFFICIENT_RANGE_SENTINEL } from '@discovr/types';
import type { Severity, LineSeverity, VariantMeta, Recommendation, Strength } from '@discovr/types';

export function severityTone(severity: Severity | LineSeverity): {
  bg: string;
  text: string;
  border: string;
  dot: string;
} {
  switch (severity) {
    case 'pass':
      return {
        bg: 'bg-emerald-100',
        text: 'text-emerald-700',
        border: 'border-emerald-300',
        dot: 'bg-emerald-500',
      };
    case 'minor':
      return {
        bg: 'bg-amber-100',
        text: 'text-amber-800',
        border: 'border-amber-300',
        dot: 'bg-amber-500',
      };
    case 'major':
      return {
        bg: 'bg-orange-100',
        text: 'text-orange-800',
        border: 'border-orange-400',
        dot: 'bg-orange-500',
      };
    case 'critical':
      return {
        bg: 'bg-rose-100',
        text: 'text-rose-800',
        border: 'border-rose-400',
        dot: 'bg-rose-500',
      };
    default:
      return {
        bg: 'bg-muted',
        text: 'text-muted-foreground',
        border: 'border-border',
        dot: 'bg-stone-400',
      };
  }
}

export function riskTone(risk: VariantMeta['risk_profile']): {
  bg: string;
  text: string;
  border: string;
} {
  switch (risk) {
    case 'low':
      return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
    case 'medium':
      return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
    case 'high':
      return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' };
    default:
      return { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border' };
  }
}

export function coverageTone(status: 'met' | 'partial' | 'missing'): {
  bg: string;
  text: string;
  border: string;
} {
  switch (status) {
    case 'met':
      return { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' };
    case 'partial':
      return { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200' };
    case 'missing':
      return { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' };
  }
}

/**
 * Fallback from legacy shape (where only `passed: boolean` was available) to
 * the richer severity enum used by the redesigned Checks tab.
 */
export function checkSeverity(check: {
  passed: boolean;
  severity?: Severity;
}): Severity {
  if (check.severity) return check.severity;
  return check.passed ? 'pass' : 'major';
}

const SCORE_BANDS: ReadonlyArray<{ min: number; swatch: string }> = [
  { min: 7, swatch: '#10b981' },
  { min: 5, swatch: '#f59e0b' },
  { min: 0, swatch: '#ef4444' },
];

/** Mini-score (0-10) to swatch: green/amber/red by threshold. */
export function scoreColor(value: number): string {
  return SCORE_BANDS.find((band) => value >= band.min)?.swatch ?? '#ef4444';
}

export function toRecommendation(s: string | undefined | null): Recommendation {
  const v = (s ?? '').trim().toLowerCase();
  if (v === 'approved') return 'approved';
  if (v === 'needs revision' || v === 'needs_revision') return 'needs_revision';
  if (v === 'rejected') return 'rejected';
  return 'unknown';
}

export function toStrength(value: number, scale: 10 | 100): Strength {
  const pct = scale === 100 ? value : value * 10;
  if (pct >= 80) return 'strong';
  if (pct >= 60) return 'fair';
  return 'weak';
}

export function aspectTone(aspect: string): {
  bg: string;
  text: string;
  border: string;
} {
  switch (aspect) {
    case 'hook':
      return { bg: 'bg-violet-50', text: 'text-violet-700', border: 'border-violet-200' };
    case 'clarity':
      return { bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-200' };
    case 'pacing':
      return { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' };
    case 'cta':
      return { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' };
    case 'compliance':
      return { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' };
    case 'brand_safety':
      return { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' };
    case 'creative_brand_voice':
      return { bg: 'bg-fuchsia-50', text: 'text-fuchsia-700', border: 'border-fuchsia-200' };
    case 'factual':
      return { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' };
    default:
      return { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border' };
  }
}

